import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, api } from '../context/AuthContext';
import { 
  TrendingUp, Users, BookOpen, FileText, Calendar, CreditCard, Mail, LogOut, Home
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { AdminUsersTab } from '../components/admin/AdminUsersTab';
import { AdminCoursesTab } from '../components/admin/AdminCoursesTab';
import { AdminResourcesTab } from '../components/admin/AdminResourcesTab';
import { AdminBookingsTab } from '../components/admin/AdminBookingsTab';
import { AdminSubscribersTab } from '../components/admin/AdminSubscribersTab';
import { AdminTransactionsTab } from '../components/admin/AdminTransactionsTab';

export default function AdminPanel() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('users');
  
  const [users, setUsers] = useState([]);
  const [courses, setCourses] = useState([]);
  const [resources, setResources] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedCourse, setSelectedCourse] = useState('');
  const [assignAction, setAssignAction] = useState('assign');

  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadForm, setUploadForm] = useState({ title: '', description: '', category: '' });
  const fileInputRef = useRef(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [usersRes, coursesRes, resourcesRes, bookingsRes, subscribersRes, transactionsRes] = await Promise.all([
        api.get('/api/admin/users'),
        api.get('/api/courses'),
        api.get('/api/resources'),
        api.get('/api/admin/bookings'),
        api.get('/api/admin/newsletter-subscribers'),
        api.get('/api/admin/transactions')
      ]);
      setUsers(usersRes.data);
      setCourses(coursesRes.data);
      setResources(resourcesRes.data);
      setBookings(bookingsRes.data);
      setSubscribers(subscribersRes.data);
      setTransactions(transactionsRes.data);
    } catch (error) {
      // Admin data fetch failed
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleCourseAssignment = async () => {
    if (!selectedUser || !selectedCourse) return;
    try {
      await api.post('/api/admin/course-assignment', {
        user_id: selectedUser.id, course_id: selectedCourse, action: assignAction
      });
      setAssignDialogOpen(false);
      setSelectedUser(null);
      setSelectedCourse('');
      fetchData();
    } catch (error) {
      // Course assignment failed
    }
  };

  const handleResourceUpload = async (e) => {
    e.preventDefault();
    const file = fileInputRef.current?.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    formData.append('title', uploadForm.title);
    formData.append('description', uploadForm.description);
    formData.append('category', uploadForm.category);
    try {
      await api.post('/api/admin/resources', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setUploadDialogOpen(false);
      setUploadForm({ title: '', description: '', category: '' });
      fetchData();
    } catch (error) {
      // Resource upload failed
    }
  };

  const handleDeleteResource = async (resourceId) => {
    if (!window.confirm('Are you sure you want to delete this resource?')) return;
    try {
      await api.delete(`/api/admin/resources/${resourceId}`);
      fetchData();
    } catch (error) {
      // Resource delete failed
    }
  };

  const handleDeleteUser = async (userId, userEmail) => {
    if (!window.confirm(`Are you sure you want to permanently delete ${userEmail}? This will remove all their data.`)) return;
    try {
      await api.delete(`/api/admin/users/${userId}`);
      fetchData();
    } catch (error) {
      // User delete failed
    }
  };

  const handleInviteUser = async (email) => {
    try {
      await api.post('/api/admin/invite-user', { email });
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Failed to send invitation' };
    }
  };

  const tabs = [
    { id: 'users', label: 'Users', icon: Users },
    { id: 'courses', label: 'Courses', icon: BookOpen },
    { id: 'resources', label: 'Resources', icon: FileText },
    { id: 'bookings', label: 'Bookings', icon: Calendar },
    { id: 'subscribers', label: 'Newsletter', icon: Mail },
    { id: 'transactions', label: 'Transactions', icon: CreditCard }
  ];

  const renderActiveTab = () => {
    switch (activeTab) {
      case 'users':
        return (
          <AdminUsersTab
            users={users} courses={courses}
            assignDialogOpen={assignDialogOpen} setAssignDialogOpen={setAssignDialogOpen}
            selectedUser={selectedUser} setSelectedUser={setSelectedUser}
            selectedCourse={selectedCourse} setSelectedCourse={setSelectedCourse}
            assignAction={assignAction} setAssignAction={setAssignAction}
            handleCourseAssignment={handleCourseAssignment}
            handleDeleteUser={handleDeleteUser}
            handleInviteUser={handleInviteUser}
          />
        );
      case 'courses':
        return <AdminCoursesTab courses={courses} />;
      case 'resources':
        return (
          <AdminResourcesTab
            resources={resources}
            uploadDialogOpen={uploadDialogOpen} setUploadDialogOpen={setUploadDialogOpen}
            uploadForm={uploadForm} setUploadForm={setUploadForm}
            fileInputRef={fileInputRef}
            handleResourceUpload={handleResourceUpload}
            handleDeleteResource={handleDeleteResource}
          />
        );
      case 'bookings':
        return <AdminBookingsTab bookings={bookings} />;
      case 'subscribers':
        return <AdminSubscribersTab subscribers={subscribers} />;
      case 'transactions':
        return <AdminTransactionsTab transactions={transactions} />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-[#09090B] flex">
      <aside className="w-64 bg-[#141417] border-r border-[#27272A] flex flex-col">
        <div className="p-6 border-b border-[#27272A]">
          <Link to="/" className="flex items-center gap-2" data-testid="admin-logo">
            <div className="w-8 h-8 bg-[#007AFF] rounded-sm flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-white" style={{ fontFamily: 'Chivo, sans-serif' }}>
              ADMIN PANEL
            </span>
          </Link>
        </div>

        <nav className="flex-1 p-4 space-y-1">
          <Link to="/dashboard" className="sidebar-link" data-testid="back-to-dashboard">
            <Home className="w-5 h-5" />
            <span>Dashboard</span>
          </Link>
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`sidebar-link w-full ${activeTab === tab.id ? 'active' : ''}`}
                data-testid={`tab-${tab.id}`}
              >
                <Icon className="w-5 h-5" />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </nav>

        <div className="p-4 border-t border-[#27272A]">
          <div className="flex items-center gap-3 mb-4 px-4">
            <div className="w-10 h-10 bg-[#FF3333] rounded-full flex items-center justify-center">
              <span className="text-white font-bold">A</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white font-medium truncate">{user?.name}</p>
              <p className="text-[#FF3333] text-sm">Admin</p>
            </div>
          </div>
          <Button
            onClick={handleLogout}
            variant="ghost"
            className="w-full justify-start text-[#A1A1AA] hover:text-white hover:bg-[#1D1D21]"
          >
            <LogOut className="w-5 h-5 mr-3" />
            Sign Out
          </Button>
        </div>
      </aside>

      <main className="flex-1 overflow-auto p-8" data-testid="admin-panel">
        {renderActiveTab()}
      </main>
    </div>
  );
}
